<script setup lang="ts">
// FAB contextuel mobile (CDC §8.6.1) : ✏️ Composer / ➕ Contact /
// ➕ Evenement / 💬 Assistant selon la route active. Visible <768px
// uniquement, au-dessus de la MobileTabBar (z-index plus eleve).

import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useChatStore } from "@/features/assistant/stores/chat-store";

const route = useRoute();
const router = useRouter();
const chat = useChatStore();

interface FabAction {
  icon: string;
  label: string;
  run: () => void;
}

const action = computed<FabAction | null>(() => {
  const path = route.path;
  if (path === "/" || path.startsWith("/?")) {
    return {
      icon: "✏️",
      label: "Composer un email",
      run: () => void router.push("/composer"),
    };
  }
  if (path === "/contacts") {
    return {
      icon: "➕",
      label: "Nouveau contact",
      run: () => void router.push({ path: "/contacts", query: { new: "1" } }),
    };
  }
  if (path === "/calendar") {
    return {
      icon: "➕",
      label: "Nouvel evenement",
      run: () => void router.push({ path: "/calendar", query: { new: "1" } }),
    };
  }
  if (path === "/assistant") {
    return {
      icon: "💬",
      label: "Nouvelle conversation",
      run: () => chat.newConversation(),
    };
  }
  return null;
});
</script>

<template>
  <button
    v-if="action"
    type="button"
    class="fab"
    :title="action.label"
    :aria-label="action.label"
    @click="action.run()"
  >
    <span aria-hidden="true">{{ action.icon }}</span>
  </button>
</template>

<style scoped>
.fab {
  display: none;
  position: fixed;
  right: 1rem;
  bottom: calc(64px + env(safe-area-inset-bottom) + 1rem);
  z-index: 35;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: none;
  background: var(--color-or);
  color: var(--color-navy);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    0 6px 18px rgba(232, 160, 32, 0.32),
    0 2px 4px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  font-size: 1.5rem;
  transition:
    background 0.18s ease,
    transform 0.12s ease,
    box-shadow 0.18s ease;
}
.fab:hover {
  background: var(--color-or-light);
}
.fab:active {
  transform: translateY(1px) scale(0.97);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 3px 8px rgba(232, 160, 32, 0.25);
}
.fab:focus-visible {
  outline: 2px solid var(--color-cream);
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .fab {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
